import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from "@angular/core";
import { NgClass } from "@angular/common";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25IfShowDirective } from "../../standalone/s25.if.show.directive";
import { DataIngestionModule } from "../data-ingestion/data.ingestion.module";
import { SeHomeComponent } from "./se.home.component";
import { IngestionPointCategory } from "../data-ingestion/data.ingestion.service";
import { MyWorkflowPageComponent } from "../workflow/my.workflow.page.component";
import { WorkflowProcessesPageComponent } from "../workflow/workflow.processes.page.component";
import { WorkflowInitRulesComponent } from "../workflow/workflow.init.rules.component";
import { WorkflowActionHistoryPageComponent } from "../workflow/actions/workflow.action.history.page.component";
import { BudgetAdminComponent } from "./budget/admin/budget.admin.component";
import { BudgetRulesComponent } from "./budget/budget.rules.component";
import { S25CheckboxModule } from "../s25-checkbox/s25.checkbox.module";
import { S25ChatComponent } from "../../standalone/s25.chat.component";
import { ApprovalGroupsComponent } from "../approval-groups/approval.groups.component";
import { UserprefService } from "../../services/userpref.service";
import { StudentEngagementService } from "./student.engagement.service";
import { WorkflowAdminComponent } from "../workflow/admin/workflow.admin.component";
import { SeSearchPageComponent } from "./search/se.search.page.component";

@TypeManagerDecorator("s25-ng-se-admin")
@Component({
    selector: "s25-ng-se-admin",
    template: `
        @if (init()) {
            @if (viewModes().length) {
                <div class="cust-attr-menu">
                    <ul>
                        @for (mode of viewModes(); track mode.id) {
                            <li [ngClass]="{ active: activeMode() === mode.id }">
                                <a href="javascript:void(0)" (click)="activeMode.set(mode.id)">{{ mode.name }}</a>
                            </li>
                        }
                    </ul>
                </div>
            }

            <div *s25IfShow="activeMode() === 'search'">
                <s25-ng-se-search-page></s25-ng-se-search-page>
            </div>

            <div *s25IfShow="activeMode() === 'chat'">
                <s25-chat [width]="'80%'" [height]="'600px'"></s25-chat>
            </div>

            <div *s25IfShow="activeMode() === 'forms'">
                <h2>Form Management</h2>
                <s25-ng-ingestion-admin
                    class="ngBlock c-margin-top--single"
                    [categoryIn]="IngestionPointCategory.ENGAGEMENT"
                ></s25-ng-ingestion-admin>
            </div>

            <div *s25IfShow="activeMode() === 'myForms'">
                <h2>My forms</h2>
                <s25-ng-ingestion-dashboard
                    class="ngBlock c-margin-top--single"
                    [selectedCategory]="IngestionPointCategory.ENGAGEMENT"
                ></s25-ng-ingestion-dashboard>
            </div>

            <div *s25IfShow="activeMode() === 'home'">
                <s25-ng-se-home></s25-ng-se-home>
            </div>

            <div *s25IfShow="activeMode() === 'budget'">
                <s25-ng-budget-admin></s25-ng-budget-admin>
            </div>

            <div *s25IfShow="activeMode() === 'budgetRules'">
                <s25-ng-budget-rules></s25-ng-budget-rules>
            </div>

            <div *s25IfShow="activeMode() === 'myWorkflow'">
                <my-workflow-page></my-workflow-page>
            </div>

            <div *s25IfShow="activeMode() === 'workflow'">
                <workflow-processes-page></workflow-processes-page>
            </div>

            <div *s25IfShow="activeMode() === 'workflowRules'">
                <workflow-init-rules></workflow-init-rules>
            </div>

            <div *s25IfShow="activeMode() === 'workflowActionHistory'">
                <workflow-action-history-page></workflow-action-history-page>
            </div>

            <div *s25IfShow="activeMode() === 'groups'">
                <approval-groups></approval-groups>
            </div>

            <div *s25IfShow="activeMode() === 'workflowAdmin'">
                <s25-ng-workflow-admin></s25-ng-workflow-admin>
            </div>
        } @else {
            <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
        }
    `,
    styles: `
        :host {
            display: flex;
            gap: 2em;
        }

        .cust-attr-menu ~ div {
            flex: 1;
            margin-left: 0;
            padding-left: 0;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25LoadingInlineModule,
        S25IfShowDirective,
        DataIngestionModule,
        SeHomeComponent,
        NgClass,
        MyWorkflowPageComponent,
        WorkflowProcessesPageComponent,
        WorkflowInitRulesComponent,
        WorkflowActionHistoryPageComponent,
        BudgetAdminComponent,
        BudgetRulesComponent,
        S25CheckboxModule,
        S25ChatComponent,
        ApprovalGroupsComponent,
        WorkflowAdminComponent,
        SeSearchPageComponent,
    ],
    providers: [StudentEngagementService],
})
export class SeAdminComponent implements OnInit {
    init = signal(false);

    activeMode = signal("home");
    isAdmin = signal(true);

    private seService = inject(StudentEngagementService);

    protected viewModes = computed(
        () =>
            this.seService.ViewModes.filter(
                (mode) => mode.showByDefault || mode.permIds.includes(this.seService?.groupId()),
            ) ?? [],
    );

    async ngOnInit(): Promise<void> {
        // TODO: check for user type (admin, student) and perms (student officer?) - use sec group for now
        const groupId = await UserprefService.getGroupId();
        this.seService.setGroupId(groupId);
        this.init.set(true);
    }

    protected readonly IngestionPointCategory = IngestionPointCategory;
}
